import { type Href, useRouter } from "expo-router";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, ScrollView, View } from "react-native";
import Animated, { LinearTransition } from "react-native-reanimated";
import { SafeAreaView } from "react-native-safe-area-context";

import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Settings } from "@/components/ui/settings";
import { Text } from "@/components/ui/text";
import { getLeavesAloneSummary } from "@/features/home/leavesAlone";
import { useChildren } from "@/features/selectedChild";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { formatIsoDate } from "@/lib/date";
import { useGetPickupRequestsForChildrenQuery } from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";

import { PickupRequestHomeCard } from "./PickupRequestHomeCard";
import { getUpcomingPickupOccurrences } from "./pickupRequestSchedule";

const LAYOUT_TRANSITION = LinearTransition.duration(200);

type ChildPickupsScreenProps = {
    childId: number;
};

export function ChildPickupsScreen({ childId }: ChildPickupsScreenProps) {
    const { t } = useTranslation();
    const router = useRouter();
    const todayIso = formatIsoDate(new Date());
    const { children, refetch: refetchChildren } = useChildren();
    const child = children.find((item) => item.id === childId);
    const {
        data: pickupRequests = [],
        isLoading: isLoadingRequests,
        refetch,
    } = useGetPickupRequestsForChildrenQuery([childId]);

    const upcoming = useMemo(
        () => getUpcomingPickupOccurrences(pickupRequests, todayIso),
        [pickupRequests, todayIso],
    );
    const leaveDaysSummary =
        getLeavesAloneSummary(child?.leaves_alone, (isoWeekday) => {
            if (child?.leaves_alone && child.leaves_alone.length > 1) {
                return t(`ui.weekdayShort.${isoWeekday}`);
            } else {
                return t(`ui.weekday.${isoWeekday}`);
            }
        }) ?? t("pickups.pickupRequired");

    const onRefreshData = useCallback(
        () => Promise.all([refetch(), refetchChildren()]),
        [refetch, refetchChildren],
    );
    const { refreshControl } = usePullToRefresh(onRefreshData);

    function handleCreatePickupRequest() {
        router.push({
            pathname: "/pickup-request",
            params: { childId: String(childId) },
        });
    }

    function handleEditLeaveDays() {
        router.push(`/child/${childId}/leave-days` as Href);
    }

    return (
        <SafeAreaView className="flex-1 bg-background" edges={["bottom"]}>
            <ScrollView
                className="flex-1"
                contentContainerClassName="gap-8 px-4 pb-6 web:mx-auto web:w-full web:max-w-lg"
                refreshControl={refreshControl}
            >
                <View className="gap-1">
                    <Text variant="muted">{t("pickups.subtitle")}</Text>
                </View>

                <Settings title={t("pickups.leaveDaysTitle")}>
                    <Settings.Item
                        label={t("pickups.leaveDays")}
                        group
                        onPress={handleEditLeaveDays}
                        testID={TEST_IDS[TEST_IDS.PICKUPS_LEAVE_DAYS_ROW]}
                    >
                        <Text
                            className="text-muted-foreground"
                            testID={TEST_IDS[TEST_IDS.PICKUPS_LEAVE_DAYS_VALUE]}
                        >
                            {leaveDaysSummary}
                        </Text>
                    </Settings.Item>
                </Settings>

                <View className="gap-2">
                    <Text
                        variant="caption"
                        className="px-1 uppercase tracking-wide"
                        testID={
                            TEST_IDS[TEST_IDS.PICKUPS_CURRENT_REQUESTS_SECTION]
                        }
                    >
                        {t("pickups.currentTitle")}
                    </Text>

                    <Accordion
                        type="single"
                        collapsible
                        className="rounded-card border border-border bg-card px-4 shadow-none"
                    >
                        {isLoadingRequests ? (
                            <View className="items-center py-8">
                                <ActivityIndicator />
                            </View>
                        ) : upcoming.length === 0 ? (
                            <View className="items-center gap-2 py-8">
                                <Text variant="label">
                                    {t("pickups.currentEmptyTitle")}
                                </Text>
                                <Text variant="muted" className="text-center">
                                    {t("pickups.currentEmptyDescription")}
                                </Text>
                            </View>
                        ) : (
                            upcoming.map(({ request, occurrenceDate }) => (
                                <PickupRequestHomeCard
                                    key={request.id}
                                    request={request}
                                    occurrenceDate={occurrenceDate}
                                />
                            ))
                        )}
                    </Accordion>
                </View>
                <Animated.View layout={LAYOUT_TRANSITION}>
                    <Button
                        className="h-auto min-h-12 py-3"
                        onPress={handleCreatePickupRequest}
                        testID={
                            TEST_IDS[
                                TEST_IDS.PICKUPS_CREATE_PICKUP_REQUEST_BUTTON
                            ]
                        }
                    >
                        <View className="flex-row items-center gap-2">
                            <Icon
                                name="plus"
                                iconStyle="solid"
                                className="text-primary-foreground"
                                size={17}
                            />
                            <Text className="text-primary-foreground">
                                {t("pickups.newRequest")}
                            </Text>
                        </View>
                    </Button>
                </Animated.View>
            </ScrollView>
        </SafeAreaView>
    );
}
